import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { LoadPanel } from "devextreme-react";
import { Vendor, getVendors } from "../../../api/support";
import { resolveVendor } from "./vendor-mock";

// Splitter altinda / ticket detayinda acilan vendor (musteri firma) bilgi karti.
// Alanlar Fores Vendor Management ekranindaki Information + Contact Information bloklariyla eslesir.
export function VendorInfoPane({ vendorRef }: { vendorRef: string }) {
    const { t } = useTranslation();
    const [vendor, setVendor] = useState<Vendor | null>(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        let alive = true;
        setLoading(true);
        // Gercek liste bos donerse (backend yok) mock fallback'a duser.
        getVendors(false)
            .then(list => { if (alive) setVendor(resolveVendor(vendorRef, list)); })
            .catch(() => { if (alive) setVendor(resolveVendor(vendorRef, [])); })
            .finally(() => { if (alive) setLoading(false); });
        return () => { alive = false; };
    }, [vendorRef]);

    if (loading) {
        return <div className="h-full w-full relative"><LoadPanel visible /></div>;
    }

    if (!vendor) {
        return (
            <div className="flex items-center justify-center h-full w-full" style={{ color: "var(--subtitle-text-color)" }}>
                {t("vendorNotFound")}
            </div>
        );
    }

    const initials = vendor.name.split(" ").map(w => w[0]).join("").toUpperCase().slice(0, 2);
    const hasStats = vendor.open_tickets != null || vendor.total_tickets != null;

    return (
        <div className="h-full w-full" style={{ overflowY: "auto", padding: 16, color: "var(--base-text-color)" }}>
            {/* Baslik */}
            <div className="flex items-center gap-3" style={{ marginBottom: 16 }}>
                <div
                    className="flex items-center justify-center shrink-0"
                    style={{ width: 48, height: 48, borderRadius: 10, background: "var(--accent-color, #6366F1)", color: "#fff", fontWeight: 600, fontSize: 16, overflow: "hidden" }}
                >
                    {vendor.image_url
                        ? <img src={vendor.image_url} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                        : initials}
                </div>
                <div className="min-w-0">
                    <div className="font-semibold truncate" style={{ fontSize: 16 }}>{vendor.name}</div>
                    <div className="text-xs truncate" style={{ color: "var(--subtitle-text-color)" }}>
                        #{vendor.ref}
                        {vendor.is_active === false && <span style={{ color: "#EF4444", marginLeft: 8 }}>• {t("passive")}</span>}
                    </div>
                </div>
            </div>

            {/* Destek baglami: ticket sayilari (yalnizca veri varsa) */}
            {hasStats && (
                <div className="grid grid-cols-2 gap-3" style={{ marginBottom: 16 }}>
                    <Stat label={t("openTickets")} value={vendor.open_tickets ?? 0} color="#F97316" />
                    <Stat label={t("totalTickets")} value={vendor.total_tickets ?? 0} color="#6366F1" />
                </div>
            )}

            {/* Information — yalnizca dolu alanlar */}
            <Section title={t("information")} emptyText={t("notDefined")} rows={[
                { label: t("vendorRefLabel"), value: `#${vendor.ref}` },
                { label: t("vendorName"), value: vendor.name },
                { label: t("vendorToken"), value: vendor.token ? "••••••••••••••••" : undefined },
                { label: t("apiUrl"), value: vendor.api_url },
                { label: t("imageUrl"), value: vendor.image_url },
            ]} />

            {/* Contact Information — yalnizca dolu alanlar */}
            <Section title={t("contactInformation")} emptyText={t("notDefined")} rows={[
                { label: t("email"), value: vendor.email },
                { label: t("phone"), value: vendor.phone },
                { label: t("contactPerson"), value: vendor.contact_person },
                { label: t("city"), value: vendor.city },
                { label: t("address"), value: vendor.address },
                { label: t("firmNr"), value: vendor.firm_nr },
                { label: t("vatNr"), value: vendor.vat_nr },
                { label: t("country"), value: vendor.country },
                { label: t("postalCode"), value: vendor.postal_code },
                { label: t("comment"), value: vendor.comment },
            ]} />
        </div>
    );
}

function Stat({ label, value, color }: { label: string; value: number; color: string }) {
    return (
        <div style={{ background: "var(--base-bg)", border: "1px solid var(--border-color)", borderRadius: 10, padding: "12px 14px" }}>
            <div style={{ fontSize: 22, fontWeight: 700, color }}>{value}</div>
            <div className="text-xs" style={{ color: "var(--subtitle-text-color)" }}>{label}</div>
        </div>
    );
}

function Section({ title, rows, emptyText }: { title: string; rows: { label: string; value?: string }[]; emptyText: string }) {
    const filled = rows.filter(r => r.value != null && r.value !== "");
    return (
        <div style={{ marginBottom: 16 }}>
            <div className="text-xs font-semibold uppercase" style={{ letterSpacing: "0.04em", color: "var(--subtitle-text-color)", marginBottom: 6 }}>
                {title}
            </div>
            <div style={{ background: "var(--base-bg)", border: "1px solid var(--border-color)", borderRadius: 10, overflow: "hidden" }}>
                {filled.length === 0 ? (
                    <div className="text-xs" style={{ padding: "12px 14px", color: "var(--subtitle-text-color)", fontStyle: "italic" }}>
                        {emptyText}
                    </div>
                ) : filled.map((r, i) => (
                    <div key={r.label}
                        className="flex items-center justify-between gap-3"
                        style={{ padding: "10px 14px", borderBottom: i === filled.length - 1 ? "none" : "1px solid var(--border-color)" }}
                    >
                        <span className="text-xs shrink-0" style={{ color: "var(--subtitle-text-color)" }}>{r.label}</span>
                        <span className="text-sm truncate" style={{ textAlign: "right" }}>{r.value}</span>
                    </div>
                ))}
            </div>
        </div>
    );
}
